import React, { useState, useEffect } from 'react';
import FeedbackDetailModal from './FeedbackDetailModal';
import apiService from '../../../services/api';
import './FeedbackPanel.scss';

const FeedbackPanel = () => {
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    search: ''
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch feedback data
  const fetchFeedback = async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await apiService.get(`/api/admin/feedback?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch feedback');
      }

      const data = await response.json();
      setFeedback(data.feedback);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await apiService.get('/api/admin/feedback/stats/summary');

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Update feedback status
  const updateFeedbackStatus = async (id, status) => {
    try {
      const response = await apiService.put(`/api/admin/feedback/${id}`, { status });

      if (!response.ok) {
        throw new Error('Failed to update feedback');
      }

      // Refresh data
      await fetchFeedback(currentPage);
      await fetchStats();
    } catch (error) {
      console.error('Error updating feedback:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle viewing feedback details
  const handleViewFeedback = (item) => {
    setSelectedFeedback(item);
    setShowDetailModal(true);
  };

  // Handle feedback update from modal
  const handleFeedbackUpdate = async (id, updates) => {
    try {
      const response = await apiService.put(`/api/admin/feedback/${id}`, updates);

      if (!response.ok) {
        throw new Error('Failed to update feedback');
      }

      // Refresh the feedback list and update selected item
      await fetchFeedback(currentPage);
      
      // Update selected feedback if it's still open
      if (selectedFeedback && selectedFeedback.id === id) {
        setSelectedFeedback(prev => ({ ...prev, ...updates }));
      }

      // Refresh stats if status was updated
      if (updates.status) {
        await fetchStats();
      }
    } catch (error) {
      console.error('Error updating feedback:', error);
      throw error;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status) {
      case 'new': return 'feedback-panel__status--new';
      case 'in_progress': return 'feedback-panel__status--progress';
      case 'resolved': return 'feedback-panel__status--resolved';
      case 'closed': return 'feedback-panel__status--closed';
      default: return 'feedback-panel__status--new';
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchFeedback(1), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (!loading) {
      fetchFeedback(1);
    }
  }, [filters]);

  if (loading) {
    return (
      <div className="feedback-panel">
        <div className="feedback-panel__loading">Loading feedback...</div>
      </div>
    );
  }

  return (
    <div className="feedback-panel">
      <div className="feedback-panel__header">
        <h3 className="feedback-panel__title">Feedback Management</h3>
        
        {/* Statistics Summary */}
        {stats && (
          <div className="feedback-panel__stats">
            <div className="feedback-panel__stat">
              <span className="feedback-panel__stat-number">{stats.total.total}</span>
              <span className="feedback-panel__stat-label">Total</span>
            </div>
            <div className="feedback-panel__stat">
              <span className="feedback-panel__stat-number">{stats.total.recent_day}</span>
              <span className="feedback-panel__stat-label">Today</span>
            </div>
            <div className="feedback-panel__stat">
              <span className="feedback-panel__stat-number">{stats.total.recent_week}</span>
              <span className="feedback-panel__stat-label">This Week</span>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="feedback-panel__filters">
        <input
          type="text"
          placeholder="Search feedback..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="feedback-panel__search"
        />
        
        <select
          value={filters.type}
          onChange={(e) => handleFilterChange('type', e.target.value)}
          className="feedback-panel__filter"
        >
          <option value="">All Types</option>
          <option value="feedback">Feedback</option>
          <option value="bug">Bug Report</option>
          <option value="feature">Feature Request</option>
          <option value="question">Question</option>
          <option value="other">Other</option>
        </select>

        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="feedback-panel__filter"
        >
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>

      </div>

      {/* Feedback Table */}
      <div className="feedback-panel__table-container">
        <table className="feedback-panel__table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Subject</th>
              <th>From</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {feedback.map(item => (
              <tr key={item.id} className="feedback-panel__row">
                <td>
                  <span className={`feedback-panel__type feedback-panel__type--${item.type}`}>
                    {item.type}
                  </span>
                </td>
                <td className="feedback-panel__subject">
                  <div className="feedback-panel__subject-text" title={item.subject}>
                    {item.subject}
                  </div>
                  <div className="feedback-panel__message-preview" title={item.message}>
                    {item.message.substring(0, 100)}...
                  </div>
                </td>
                <td>
                  <div className="feedback-panel__user">
                    <div>{item.username || 'Anonymous'}</div>
                    {item.email && (
                      <div className="feedback-panel__email">{item.email}</div>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`feedback-panel__status ${getStatusClass(item.status)}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="feedback-panel__date">
                  {formatDate(item.created_at)}
                </td>
                <td>
                  <div className="feedback-panel__actions">
                    <button
                      onClick={() => handleViewFeedback(item)}
                      className="feedback-panel__action-btn feedback-panel__action-btn--view"
                      title="View Details"
                    >
                      View
                    </button>
                    {item.status === 'new' && (
                      <button
                        onClick={() => updateFeedbackStatus(item.id, 'in_progress')}
                        className="feedback-panel__action-btn feedback-panel__action-btn--start"
                        title="Mark as In Progress"
                      >
                        Start
                      </button>
                    )}
                    {item.status === 'in_progress' && (
                      <button
                        onClick={() => updateFeedbackStatus(item.id, 'resolved')}
                        className="feedback-panel__action-btn feedback-panel__action-btn--resolve"
                        title="Mark as Resolved"
                      >
                        Resolve
                      </button>
                    )}
                    {item.status === 'resolved' && (
                      <button
                        onClick={() => updateFeedbackStatus(item.id, 'closed')}
                        className="feedback-panel__action-btn feedback-panel__action-btn--close"
                        title="Close"
                      >
                        Close
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {feedback.length === 0 && (
          <div className="feedback-panel__empty">
            No feedback found matching the current filters.
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="feedback-panel__pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="feedback-panel__page-btn"
          >
            Previous
          </button>
          
          <span className="feedback-panel__page-info">
            Page {currentPage} of {pagination.pages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
            disabled={currentPage === pagination.pages}
            className="feedback-panel__page-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Feedback Detail Modal */}
      {showDetailModal && selectedFeedback && (
        <FeedbackDetailModal
          feedback={selectedFeedback}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedFeedback(null);
          }}
          onUpdate={handleFeedbackUpdate}
        />
      )}
    </div>
  );
};

export default FeedbackPanel;